import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { login, logout, getMe } from "./authSlice";
import type { ModuleKey } from "../../constants/modules";
import type { User } from "../../types";

/**
 * The tenant's enabled module set, as this session currently understands it.
 *
 * ONE SOURCE OF TRUTH, deliberately. The set arrives on the populated
 * `user.companyId` from login and /auth/me, and also over the socket when a
 * superadmin changes it mid-session. Reading it straight off `useAuth().user`
 * would work for the first two and silently miss the third, leaving somebody
 * on a nav they are no longer entitled to until they next signed in. So every
 * source funnels into this slice, and `useModules` reads only from here.
 *
 * `null` means "not known yet" — signed out, or a tenant created before module
 * management existed. It is resolved as "everything enabled" (see
 * `resolveEnabledModules`), matching the backend, because the guard is what
 * actually enforces access; the UI only decides what to offer.
 */
interface ModulesState {
  enabled: ModuleKey[] | null;
}

/** The company arrives populated on the user, or as a bare id string. */
const readFromUser = (user: unknown): ModuleKey[] | null => {
  const company = (user as User | null | undefined)?.companyId;
  if (!company || typeof company !== "object") return null;

  const modules = (company as { enabledModules?: unknown }).enabledModules;
  return Array.isArray(modules) ? (modules as ModuleKey[]) : null;
};

const readFromStorage = (): ModuleKey[] | null => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? readFromUser(JSON.parse(raw)) : null;
  } catch {
    // A corrupt entry is not worth failing the boot over; "unknown" is safe.
    return null;
  }
};

const initialState: ModulesState = { enabled: readFromStorage() };

const modulesSlice = createSlice({
  name: "modules",
  initialState,
  reducers: {
    /**
     * A superadmin changed this tenant's modules while the app was open.
     * Dispatched from the socket bridge; see `emitModulesChanged` on the server.
     */
    modulesChanged: (state, action: PayloadAction<ModuleKey[]>) => {
      state.enabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login returns { token, user, company }; the module set rides on the
      // populated user, same as it does on /auth/me.
      .addCase(login.fulfilled, (state, action) => {
        state.enabled = readFromUser(action.payload?.user);
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.enabled = readFromUser(action.payload);
      })
      // Back to "unknown" rather than to a stale tenant's set — the next login
      // may well be a different company on the same device.
      .addCase(logout.fulfilled, (state) => {
        state.enabled = null;
      })
      .addCase(logout.rejected, (state) => {
        state.enabled = null;
      });
  },
});

export const { modulesChanged } = modulesSlice.actions;
export default modulesSlice.reducer;
